import { useState, useRef, useMemo, useEffect } from "react";
import { useDataCleaningContext } from "../context/data-cleaning-context";
import useCleaningData from "./use-cleaning-data";
import { DEFAULT_DISPLAY_LIMIT } from "../constants";

interface EntityData {
  id: string;
  name: string;
  count: number;
  selected: boolean;
}

interface UseEntitySelectionReturn {
  entities: EntityData[];
  displayedEntities: EntityData[];
  showAll: boolean;
  isLoading: boolean;
  error: Error | undefined;
  hasSelectedClient: boolean;
  handleEntityToggle: (id: string) => void;
  handleClearSelection: () => void;
  handleShowAllToggle: () => void;
  refetch: () => void;
}

const useEntitySelection = (
  hasSelectedClient: boolean,
): UseEntitySelectionReturn => {
  const { selection, setSelectedEntity, setSelectedClients } =
    useDataCleaningContext();
  const { getFilteredEntities, isLoading, error, refetch } = useCleaningData();

  const [showAll, setShowAll] = useState(false);
  const hasAdvanced = useRef(false);

  // Get filtered entities based on selected clients
  const apiEntities = useMemo(() => {
    if (selection.selectedClientIds.length === 0) return [];
    return getFilteredEntities(selection.selectedClientIds);
  }, [selection.selectedClientIds, getFilteredEntities]);

  // Transform API entities to local format (without icon)
  const initialEntities = useMemo(
    () =>
      apiEntities.map((entity) => ({
        id: `${entity.clientId}-${entity.entityName}`,
        name: entity.entityName,
        count: entity.fieldData.length,
        selected: false,
      })),
    [apiEntities],
  );

  const [entities, setEntities] = useState<EntityData[]>(initialEntities);

  // Update entities state when API data changes, but preserve selection from context
  useEffect(() => {
    const updatedEntities = initialEntities.map((entity) => {
      // Check if this entity matches the one selected in context
      const isSelected =
        selection.selectedEntityId !== undefined &&
        selection.selectedEntity !== undefined &&
        entity.name === selection.selectedEntity;

      return {
        ...entity,
        selected: isSelected,
      };
    });
    setEntities(updatedEntities);
  }, [initialEntities, selection.selectedEntityId, selection.selectedEntity]);

  const handleEntityToggle = (id: string) => {
    const clickedEntity = entities.find((entity) => entity.id === id);
    if (clickedEntity?.selected) {
      return; // Don't allow unselecting
    }

    const updatedEntities = entities.map((entity) => ({
      ...entity,
      selected: entity.id === id,
    }));
    setEntities(updatedEntities);

    // Save selected entity to context
    const selectedEntity = apiEntities.find(
      (e) => `${e.clientId}-${e.entityName}` === id,
    );
    if (selectedEntity) {
      // Pass both entityId and entityName to context
      setSelectedEntity(selectedEntity.entityId, selectedEntity.entityName);

      // Refetch cleaning data to ensure counts are up to date
      refetch();
    }
  };

  const handleClearSelection = () => {
    // Reset to initial state
    setEntities(initialEntities);
    hasAdvanced.current = false;
    // Trigger context reset for entity and field (cascades from setSelectedClients)
    setSelectedClients(selection.selectedClientIds);
    // Refetch to ensure fresh data
    refetch();
  };

  const handleShowAllToggle = () => {
    setShowAll(!showAll);
  };

  const displayedEntities = showAll
    ? entities
    : entities.slice(0, DEFAULT_DISPLAY_LIMIT);

  return {
    entities,
    displayedEntities,
    showAll,
    isLoading,
    error,
    hasSelectedClient,
    handleEntityToggle,
    handleClearSelection,
    handleShowAllToggle,
    refetch,
  };
};

export default useEntitySelection;
